import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { isAuthenticated } from '../utils/auth';
import Navbar from '../components/common/Navbar';
import { ClipboardDocumentIcon, LinkIcon } from '@heroicons/react/24/outline';

const fmtRp = (n) => 'Rp ' + Number(n || 0).toLocaleString('id-ID');

function StatusBadge({ status }) {
  const map = {
    pending:   'bg-yellow-100 text-yellow-800',
    available: 'bg-green-100 text-green-800',
    paid:      'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

const TABS = ['KATALOG', 'LINK SAYA', 'KOMISI', 'PENARIKAN'];

export default function AffiliateDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading]       = useState(true);
  const [affiliate, setAffiliate]   = useState(null);
  const [notFound, setNotFound]     = useState(false);
  const [regForm, setRegForm]       = useState({ bank_name: '', bank_account_number: '', bank_account_name: '' });
  const [regLoading, setRegLoading] = useState(false);
  const [regDone, setRegDone]       = useState(false);

  const [tab, setTab]               = useState('KATALOG');
  const [stats, setStats]           = useState(null);
  const [ebooks, setEbooks]         = useState([]);
  const [links, setLinks]           = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);

  // withdrawal form pre-filled from affiliate bank info
  const [wdForm, setWdForm] = useState({ amount: '', bank_name: '', bank_account_number: '', bank_account_name: '' });
  const [wdLoading, setWdLoading] = useState(false);

  // modal for newly created link
  const [linkModal, setLinkModal]   = useState(null); // { url }

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
    api.get('/api/affiliate/me')
      .then(r => { setAffiliate(r.data); setLoading(false); })
      .catch(err => {
        if (err.response?.status === 404) { setNotFound(true); }
        else { toast.error('Gagal memuat data afiliasi'); }
        setLoading(false);
      });
  }, [navigate]);

  // load tab data when affiliate active
  useEffect(() => {
    if (affiliate?.status !== 'active') return;
    api.get('/api/affiliate/dashboard')
      .then(r => setStats(r.data))
      .catch(() => toast.error('Gagal memuat statistik'));
  }, [affiliate]);

  useEffect(() => {
    if (affiliate?.status !== 'active') return;
    if (tab === 'KATALOG') {
      api.get('/api/affiliate/ebooks').then(r => setEbooks(r.data)).catch(() => toast.error('Gagal memuat katalog'));
    } else if (tab === 'LINK SAYA') {
      api.get('/api/affiliate/links').then(r => setLinks(r.data)).catch(() => toast.error('Gagal memuat link'));
    } else if (tab === 'KOMISI') {
      api.get('/api/affiliate/commissions').then(r => setCommissions(r.data)).catch(() => toast.error('Gagal memuat komisi'));
    } else if (tab === 'PENARIKAN') {
      api.get('/api/affiliate/withdrawals').then(r => setWithdrawals(r.data)).catch(() => toast.error('Gagal memuat penarikan'));
      // pre-fill bank info
      if (affiliate) setWdForm(f => ({
        ...f,
        bank_name: affiliate.bank_name || '',
        bank_account_number: affiliate.bank_account_number || '',
        bank_account_name: affiliate.bank_account_name || '',
      }));
    }
  }, [tab, affiliate]);

  async function register(e) {
    e.preventDefault();
    setRegLoading(true);
    try {
      await api.post('/api/affiliate/register', regForm);
      setRegDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(', ') : (d || 'Pendaftaran gagal'));
    } finally {
      setRegLoading(false);
    }
  }

  async function promosikan(ebook_id) {
    try {
      const r = await api.post('/api/affiliate/links', { ebook_id });
      const url = window.location.origin + '/go/' + r.data.code;
      setLinkModal({ url });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(', ') : (d || 'Gagal membuat link'));
    }
  }

  function copyLink(url) {
    navigator.clipboard.writeText(url).then(() => toast.success('Link disalin!'));
  }

  async function submitWithdrawal(e) {
    e.preventDefault();
    setWdLoading(true);
    try {
      await api.post('/api/affiliate/withdrawals', { ...wdForm, amount: Number(wdForm.amount) });
      toast.success('Permintaan penarikan berhasil');
      setWdForm(f => ({ ...f, amount: '' }));
      const r = await api.get('/api/affiliate/withdrawals');
      setWithdrawals(r.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(', ') : (d || 'Penarikan gagal'));
    } finally {
      setWdLoading(false);
    }
  }

  if (loading) return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8 text-gray-500">Memuat...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Program Afiliasi</h1>

        {/* ── Registration form ── */}
        {notFound && !regDone && (
          <div className="bg-white rounded-lg shadow p-6 max-w-md">
            <h2 className="text-lg font-semibold mb-4">Daftar sebagai Afiliasi</h2>
            <form onSubmit={register} className="space-y-4">
              {[
                { name: 'bank_name', label: 'Nama Bank' },
                { name: 'bank_account_number', label: 'Nomor Rekening' },
                { name: 'bank_account_name', label: 'Nama Pemilik Rekening' },
              ].map(f => (
                <div key={f.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                  <input
                    required
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={regForm[f.name]}
                    onChange={e => setRegForm(p => ({ ...p, [f.name]: e.target.value }))}
                  />
                </div>
              ))}
              <button
                type="submit"
                disabled={regLoading}
                className="w-full bg-blue-600 text-white py-2 rounded font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {regLoading ? 'Mendaftar...' : 'Daftar'}
              </button>
            </form>
          </div>
        )}

        {regDone && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-green-800">
            Pendaftaran berhasil, menunggu persetujuan admin.
          </div>
        )}

        {/* ── Pending status ── */}
        {affiliate?.status === 'pending' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-yellow-800">
            <p className="font-semibold">Menunggu persetujuan admin</p>
            <p className="text-sm mt-1">Akun afiliasi Anda sedang ditinjau. Silakan cek kembali nanti.</p>
          </div>
        )}

        {/* ── Active dashboard ── */}
        {affiliate?.status === 'active' && (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total Klik',        value: stats?.total_clicks ?? '–' },
                { label: 'Total Order',       value: stats?.total_orders ?? '–' },
                { label: 'Komisi Pending',    value: fmtRp(stats?.pending_commission) },
                { label: 'Komisi Tersedia',   value: fmtRp(stats?.available_commission) },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-lg shadow p-6">
                  <p className="text-xs text-gray-500 mb-1">{s.label}</p>
                  <p className="text-xl font-bold text-gray-800">{s.value}</p>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow">
              <div className="flex border-b">
                {TABS.map(t => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* ── KATALOG ── */}
                {tab === 'KATALOG' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {ebooks.map(eb => (
                      <div key={eb.id} className="border rounded-lg overflow-hidden flex flex-col">
                        {eb.cover_url && (
                          <img src={eb.cover_url} alt={eb.title} className="w-full h-40 object-cover" />
                        )}
                        <div className="p-4 flex flex-col flex-1">
                          <p className="font-semibold text-sm text-gray-800 line-clamp-2">{eb.title}</p>
                          <p className="text-blue-600 font-bold mt-1">{fmtRp(eb.final_price ?? eb.price)}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {eb.commission_type === 'percent'
                              ? `${eb.commission_value}% per penjualan`
                              : `${fmtRp(eb.commission_value)} per penjualan`}
                          </p>
                          <button
                            onClick={() => promosikan(eb.id)}
                            className="mt-auto pt-3 w-full bg-blue-600 text-white text-sm py-1.5 rounded hover:bg-blue-700"
                          >
                            Promosikan
                          </button>
                        </div>
                      </div>
                    ))}
                    {ebooks.length === 0 && <p className="text-gray-400 col-span-3">Belum ada ebook tersedia.</p>}
                  </div>
                )}

                {/* ── LINK SAYA ── */}
                {tab === 'LINK SAYA' && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 uppercase">
                          <th className="pb-2 pr-4">Judul Ebook</th>
                          <th className="pb-2 pr-4">Kode</th>
                          <th className="pb-2 pr-4">Klik</th>
                          <th className="pb-2 pr-4">Klik Unik</th>
                          <th className="pb-2 pr-4">Komisi/Penjualan</th>
                          <th className="pb-2">URL</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {links.map(lk => {
                          const url = window.location.origin + '/go/' + lk.code;
                          return (
                            <tr key={lk.id || lk.code}>
                              <td className="py-2 pr-4">{lk.ebook_title}</td>
                              <td className="py-2 pr-4 font-mono text-xs">{lk.code}</td>
                              <td className="py-2 pr-4">{lk.clicks ?? 0}</td>
                              <td className="py-2 pr-4">{lk.unique_clicks ?? 0}</td>
                              <td className="py-2 pr-4">
                                {lk.commission_type === 'percent'
                                  ? `${lk.commission_value}%`
                                  : fmtRp(lk.commission_value)}
                              </td>
                              <td className="py-2 flex items-center gap-2">
                                <span className="text-xs text-gray-500 truncate max-w-[160px]">{url}</span>
                                <button onClick={() => copyLink(url)} title="Salin">
                                  <ClipboardDocumentIcon className="w-4 h-4 text-blue-500 hover:text-blue-700" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {links.length === 0 && (
                          <tr><td colSpan={6} className="py-4 text-gray-400">Belum ada link.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ── KOMISI ── */}
                {tab === 'KOMISI' && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 uppercase">
                          <th className="pb-2 pr-4">Ebook</th>
                          <th className="pb-2 pr-4">Jumlah</th>
                          <th className="pb-2 pr-4">Status</th>
                          <th className="pb-2 pr-4">Tanggal</th>
                          <th className="pb-2">Tersedia</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {commissions.map(cm => (
                          <tr key={cm.id}>
                            <td className="py-2 pr-4">{cm.ebook_title}</td>
                            <td className="py-2 pr-4 font-semibold">{fmtRp(cm.amount)}</td>
                            <td className="py-2 pr-4"><StatusBadge status={cm.status} /></td>
                            <td className="py-2 pr-4 text-gray-500">
                              {cm.created_at ? new Date(cm.created_at).toLocaleDateString('id-ID') : '–'}
                            </td>
                            <td className="py-2 text-gray-500">
                              {cm.available_at ? new Date(cm.available_at).toLocaleDateString('id-ID') : '–'}
                            </td>
                          </tr>
                        ))}
                        {commissions.length === 0 && (
                          <tr><td colSpan={5} className="py-4 text-gray-400">Belum ada komisi.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* ── PENARIKAN ── */}
                {tab === 'PENARIKAN' && (
                  <div className="space-y-6">
                    {/* Balance summary */}
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { label: 'Komisi Pending',  value: fmtRp(stats?.pending_commission) },
                        { label: 'Tersedia',         value: fmtRp(stats?.available_commission) },
                        { label: 'Sudah Dibayar',    value: fmtRp(stats?.paid_commission) },
                      ].map(s => (
                        <div key={s.label} className="bg-gray-50 rounded-lg p-4 text-center">
                          <p className="text-xs text-gray-500">{s.label}</p>
                          <p className="font-bold text-gray-800 mt-1">{s.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Withdrawal form */}
                    <form onSubmit={submitWithdrawal} className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <h3 className="font-semibold text-gray-700">Ajukan Penarikan</h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah (Rp)</label>
                        <input
                          type="number"
                          required
                          min={1}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={wdForm.amount}
                          onChange={e => setWdForm(p => ({ ...p, amount: e.target.value }))}
                        />
                      </div>
                      {[
                        { name: 'bank_name', label: 'Nama Bank' },
                        { name: 'bank_account_number', label: 'Nomor Rekening' },
                        { name: 'bank_account_name', label: 'Nama Pemilik Rekening' },
                      ].map(f => (
                        <div key={f.name}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                          <input
                            required
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={wdForm[f.name]}
                            onChange={e => setWdForm(p => ({ ...p, [f.name]: e.target.value }))}
                          />
                        </div>
                      ))}
                      <button
                        type="submit"
                        disabled={wdLoading}
                        className="bg-blue-600 text-white px-6 py-2 rounded font-medium hover:bg-blue-700 disabled:opacity-50"
                      >
                        {wdLoading ? 'Memproses...' : 'Ajukan Penarikan'}
                      </button>
                    </form>

                    {/* Past withdrawals */}
                    <div className="overflow-x-auto">
                      <h3 className="font-semibold text-gray-700 mb-3">Riwayat Penarikan</h3>
                      <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500 uppercase">
                            <th className="pb-2 pr-4">Jumlah</th>
                            <th className="pb-2 pr-4">Bank</th>
                            <th className="pb-2 pr-4">Rekening</th>
                            <th className="pb-2 pr-4">Status</th>
                            <th className="pb-2">Tanggal</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {withdrawals.map(wd => (
                            <tr key={wd.id}>
                              <td className="py-2 pr-4 font-semibold">{fmtRp(wd.amount)}</td>
                              <td className="py-2 pr-4">{wd.bank_name}</td>
                              <td className="py-2 pr-4 font-mono text-xs">{wd.bank_account_number}</td>
                              <td className="py-2 pr-4"><StatusBadge status={wd.status} /></td>
                              <td className="py-2 text-gray-500">
                                {wd.created_at ? new Date(wd.created_at).toLocaleDateString('id-ID') : '–'}
                              </td>
                            </tr>
                          ))}
                          {withdrawals.length === 0 && (
                            <tr><td colSpan={5} className="py-4 text-gray-400">Belum ada penarikan.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Link modal */}
      {linkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-blue-500" /> Link Afiliasi Anda
            </h3>
            <div className="flex items-center gap-2 bg-gray-50 border rounded px-3 py-2">
              <span className="text-sm text-gray-700 truncate flex-1">{linkModal.url}</span>
              <button onClick={() => copyLink(linkModal.url)} className="shrink-0">
                <ClipboardDocumentIcon className="w-5 h-5 text-blue-500 hover:text-blue-700" />
              </button>
            </div>
            <button
              onClick={() => setLinkModal(null)}
              className="mt-4 w-full border border-gray-300 text-gray-700 py-2 rounded hover:bg-gray-50"
            >
              Tutup
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
